/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CreateExternalGlossaryCollectionResponseDtoStatusEnum,
  GlossaryCollectionDto,
  GlossaryCollectionDtoStatusEnum,
  GlossaryCollectionDtoTypeEnum,
} from '@ink-ai/insight-service-sdk';
import { formatDate } from '@ink-ai/portal/common/utils';
import { PaginatedTable } from '../../../common/components/paginated-table/PaginatedTable';
import { EllipsisCell } from '../../../common/components/paginated-table/EllipsisCell';
import { CopyableId } from '../../../common/components/CopyableId';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import {
  listGlossaryCollections,
  statusClassMapping,
} from '@ink-ai/portal/reducers/glossary';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { DeleteOutlineOutlined, FileDownloadOutlined, DriveFileMoveOutlined, SyncOutlined } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import { app } from '@ink-ai/portal/reducers/app';

export const statusIconMapping: Record<
  CreateExternalGlossaryCollectionResponseDtoStatusEnum,
  React.ReactNode
> = {
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.PendingUpload]: (
    <PendingOutlinedIcon />
  ),
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Importing]: (
    <PendingOutlinedIcon />
  ),
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Ready]: (
    <CheckCircleOutlineOutlinedIcon />
  ),
  [CreateExternalGlossaryCollectionResponseDtoStatusEnum.Error]: (
    <ErrorOutlineOutlinedIcon />
  ),
};

// Explorer mixed item type to support folders and glossaries uniformly
type ExplorerItem = {
  uuid: string;
  name: string;
  type: 'folder' | 'glossary';
  desc?: string;
  status?: GlossaryCollectionDtoStatusEnum;
  itemCount?: number;
  updatedAt?: string;
  parentId?: string | null;
};

interface GlossaryCollectionTableProps {
  selectedGlossaries: string[];
  selectedFolders?: string[];
  searchTerm: string;
  onNavigate?: (glossary: GlossaryCollectionDto) => void;
  onEnterFolder?: (folder: { uuid: string; name?: string; parentId?: string | null }) => void;
  onMoveClick?: (glossary?: GlossaryCollectionDto) => void;
  onDownloadClick?: (glossary: GlossaryCollectionDto) => void;
  onDeleteClick?: (glossary: GlossaryCollectionDto) => void;
  onUpdateClick?: (glossary: GlossaryCollectionDto) => void;
  onDeleteFolderClick?: (folderId: string) => void;
  page: number;
  rowsPerPage?: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSelectedGlossariesChange: (selectedGlossaries: string[]) => void;
  onSelectedFoldersChange?: (selectedFolders: string[]) => void;
  onTotalItemCountChange?: (total: number) => void;
  allowNonReadySelection?: boolean;
  enableSelection?: boolean;
  showActions?: boolean;
  // Per-row mutability gate. When it returns false for a row (e.g. a "default"
  // collection/folder for a non-super-admin), that row's edit/delete/rename
  // actions are hidden — only read-only actions (download) remain.
  canMutateRow?: (item: any) => boolean;
  rowActionLoading?: boolean;
  addingFolder?: boolean;
  draftFolderName?: string;
  onDraftFolderNameChange?: (name: string) => void;
  onConfirmAddFolder?: () => void;
  onCancelAddFolder?: () => void;
  // Optional future data source: mixed explorer items (folders + glossaries)
  mixedItems?: ExplorerItem[];
  // Optional total count for server-driven pagination
  totalCount?: number;
  // Enable cascading selection: selecting a folder toggles its direct children items
  cascadeSelection?: boolean;
  // 当前视图所在的文件夹 ID，用于 parentId 缺失时的回退同步
  currentFolderId?: string | null;
  onReloadExplorer?: () => void;
}

export const GlossaryCollectionTable: React.FC<
  GlossaryCollectionTableProps
> = ({
  selectedGlossaries,
  selectedFolders = [],
  onSelectedGlossariesChange,
  onSelectedFoldersChange,
  searchTerm,
  onNavigate,
  onEnterFolder,
  onMoveClick,
  onDownloadClick,
  onDeleteClick,
  onUpdateClick,
  onDeleteFolderClick,
  page,
  rowsPerPage: propsRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onTotalItemCountChange,
  allowNonReadySelection = true,
  enableSelection = false,
  showActions = false,
  canMutateRow,
  rowActionLoading = false,
  addingFolder = false,
  draftFolderName = '',
  onDraftFolderNameChange,
  onConfirmAddFolder,
  onCancelAddFolder,
  mixedItems,
  totalCount,
  cascadeSelection = false,
  currentFolderId,
  onReloadExplorer,
}) => {
  const dispatch = useDispatch<any>();
  const glossary = useSelector((state: RootState) => state.glossary);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(10);
  const rowsPerPage = propsRowsPerPage ?? localRowsPerPage;

  const formatter = new Intl.NumberFormat('en-US');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState<string>('');

  // 当未提供 mixedItems（仍使用旧的本地列表）时，才触发原来的列表请求
  useEffect(() => {
    if (!mixedItems) {
      dispatch(listGlossaryCollections());
    }
  }, [dispatch, mixedItems]);

  useEffect(() => {
    if (onTotalItemCountChange) {
      const internalItemCount = selectedGlossaries.reduce((sum, uuid) => {
        const glossaryItem = glossary.glossaries.find((g) => g.uuid === uuid);
        if (
          glossaryItem &&
          glossaryItem.type !== GlossaryCollectionDtoTypeEnum.External
        ) {
          return sum + (glossaryItem.itemCount || 0);
        }
        return sum;
      }, 0);
      onTotalItemCountChange(internalItemCount);
    }
  }, [selectedGlossaries, glossary.glossaries, onTotalItemCountChange]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // 服务端分页（mixedItems 存在）时不再在前端切片
      const isServerDriven = !!mixedItems;
      const currentPageItems = isServerDriven
        ? filteredItems
        : filteredItems.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
          );
      const selectableGlossaries = currentPageItems.filter((it) => !(typeof (it as any).type === 'string' && (it as any).type === 'folder') && (it as any).uuid !== '__draft__');
      const selectableFolders = currentPageItems.filter((it) => typeof (it as any).type === 'string' && (it as any).type === 'folder' && (it as any).uuid !== '__draft__');
      const newSelectedGlossaries = selectableGlossaries.map((g: any) => g.uuid);
      const newSelectedFolders = selectableFolders.map((f: any) => f.uuid);
      onSelectedGlossariesChange(newSelectedGlossaries);
      onSelectedFoldersChange && onSelectedFoldersChange(newSelectedFolders);
    } else {
      onSelectedGlossariesChange([]);
      onSelectedFoldersChange && onSelectedFoldersChange([]);
    }
  };

  

  const handleClick = async (uuid: string) => {
    if (uuid === '__draft__') return;
    // Find item from current source to check type/status
    const allItems: any[] = mixedItems ?? (glossary.glossaries as any[]) ?? [];
    const item = allItems.find((g) => g.uuid === uuid);
    const isFolder = typeof item?.type === 'string' && item?.type === 'folder';
    if (isFolder) {
      const selectedIndex = selectedFolders.indexOf(uuid);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedFolders, uuid);
        // Cascade: when selecting a folder, also select its direct child glossaries
        if (cascadeSelection) {
          const inListChildren: any[] = (baseItems || []).filter((it: any) => it.parentId === uuid && !(typeof it.type === 'string' && it.type === 'folder'));
          const hiddenChildren: any[] = Array.isArray(item?.children) ? item.children : [];
          const mergedChildren = [...inListChildren, ...hiddenChildren];
          const allowedChildIds = mergedChildren
            .filter((it: any) => allowNonReadySelection || it.status === GlossaryCollectionDtoStatusEnum.Ready)
            .map((it: any) => it.uuid);
          // De-duplicate and merge into selectedGlossaries
          const merged = Array.from(new Set([...selectedGlossaries, ...allowedChildIds]));
          onSelectedGlossariesChange(merged);
        }
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedFolders.slice(1));
        // Cascade: when deselecting, remove its direct child glossaries
        if (cascadeSelection) {
          const inListChildren: any[] = (baseItems || []).filter((it: any) => it.parentId === uuid && !(typeof it.type === 'string' && it.type === 'folder'));
          const hiddenChildren: any[] = Array.isArray(item?.children) ? item.children : [];
          const childIds = [...inListChildren, ...hiddenChildren].map((it: any) => it.uuid);
          const pruned = selectedGlossaries.filter((id) => !childIds.includes(id));
          onSelectedGlossariesChange(pruned);
        }
      } else if (selectedIndex === selectedFolders.length - 1) {
        newSelected = newSelected.concat(selectedFolders.slice(0, -1));
        if (cascadeSelection) {
          const inListChildren: any[] = (baseItems || []).filter((it: any) => it.parentId === uuid && !(typeof it.type === 'string' && it.type === 'folder'));
          const hiddenChildren: any[] = Array.isArray(item?.children) ? item.children : [];
          const childIds = [...inListChildren, ...hiddenChildren].map((it: any) => it.uuid);
          const pruned = selectedGlossaries.filter((id) => !childIds.includes(id));
          onSelectedGlossariesChange(pruned);
        }
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedFolders.slice(0, selectedIndex),
          selectedFolders.slice(selectedIndex + 1),
        );
        if (cascadeSelection) {
          const inListChildren: any[] = (baseItems || []).filter((it: any) => it.parentId === uuid && !(typeof it.type === 'string' && it.type === 'folder'));
          const hiddenChildren: any[] = Array.isArray(item?.children) ? item.children : [];
          const childIds = [...inListChildren, ...hiddenChildren].map((it: any) => it.uuid);
          const pruned = selectedGlossaries.filter((id) => !childIds.includes(id));
          onSelectedGlossariesChange(pruned);
        }
      }
      onSelectedFoldersChange && onSelectedFoldersChange(newSelected);
      return;
    }

    if (
      !allowNonReadySelection &&
      item?.status !== GlossaryCollectionDtoStatusEnum.Ready
    ) {
      return;
    }
    const selectedIndex = selectedGlossaries.indexOf(uuid);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedGlossaries, uuid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedGlossaries.slice(1));
    } else if (selectedIndex === selectedGlossaries.length - 1) {
      newSelected = newSelected.concat(selectedGlossaries.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedGlossaries.slice(0, selectedIndex),
        selectedGlossaries.slice(selectedIndex + 1),
      );
    }

    onSelectedGlossariesChange(newSelected);
  };

  const handlePageChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    newPage: number,
  ) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    } else {
      setLocalRowsPerPage(newRowsPerPage);
    }
    onPageChange(0);
  };

  // Base items: prefer mixed explorer items if provided; fallback to glossary list
  const baseItems: any[] = useMemo(() => {
    return (mixedItems as any[]) ?? (glossary.glossaries as any[]) ?? [];
  }, [mixedItems, glossary.glossaries]);

  const filteredItems = useMemo(
    () =>
      (baseItems || []).filter((g: any) =>
        (g?.name || '').toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [baseItems, searchTerm],
  );

  const displayItems = useMemo(() => {
    if (!addingFolder) return filteredItems;
    const draftRow: any = {
      uuid: '__draft__',
      name: draftFolderName ?? '',
      desc: '',
      type: 'folder',
      status: GlossaryCollectionDtoStatusEnum.Ready,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      itemCount: 0,
    } as any;
    return [draftRow, ...filteredItems];
  }, [addingFolder, draftFolderName, filteredItems]);

  // 当处于某个文件夹视图时，如果该文件夹的子项在当前视图中全部取消勾选，则同步取消该父文件夹的勾选
  useEffect(() => {
    if (!enableSelection || !cascadeSelection || !onSelectedFoldersChange) {
      return;
    }
    // 仅在明确进入了某个文件夹视图时进行父子同步（根目录不进行取消勾选）
    if (!currentFolderId) return;

    const pageParents = Array.from(
      new Set(
        (displayItems || [])
          .map((it: any) => it.parentId)
          .filter((id: any): id is string => !!id),
      ),
    );
    const hasChildrenInView = (displayItems || []).some((it: any) => !(typeof it.type === 'string' && it.type === 'folder'));
    const parentIds = pageParents.length > 0 ? pageParents : (currentFolderId && hasChildrenInView ? [currentFolderId] : []);
    if (parentIds.length === 0) return;

    const nextSelectedFolders = [...selectedFolders];
    let changed = false;
    console.debug('[GlossarySync] context', {
      currentFolderId,
      page,
      searchTerm,
      displayCount: (displayItems || []).length,
      parentIds,
    });
    parentIds.forEach((folderId) => {
      if (nextSelectedFolders.includes(folderId)) {
        const childInViewCount = (displayItems || []).filter((it: any) => it.parentId === folderId && it.type !== 'folder').length;
        const selectedChildCount = (displayItems || []).filter((it: any) => it.parentId === folderId && it.type !== 'folder' && selectedGlossaries.includes(it.uuid)).length;
        console.debug('[GlossarySync] check', { folderId, childInViewCount, selectedChildCount });
        if (childInViewCount > 0 && selectedChildCount === 0) {
          const idx = nextSelectedFolders.indexOf(folderId);
          if (idx > -1) {
            nextSelectedFolders.splice(idx, 1);
            changed = true;
          }
        }
      }
    });

    if (changed) {
      console.info('[GlossarySync] apply', { before: selectedFolders, after: nextSelectedFolders });
      onSelectedFoldersChange(nextSelectedFolders);
    }
  }, [displayItems, selectedGlossaries, selectedFolders, enableSelection, cascadeSelection, onSelectedFoldersChange, currentFolderId, page, searchTerm]);

  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: t('table.name'),
        width: 250,
        render: (item: any) => {
          if (item.uuid === '__draft__') {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <FolderIcon fontSize="small" />
                <TextField
                  size="small"
                  value={draftFolderName}
                  onChange={(e) => onDraftFolderNameChange && onDraftFolderNameChange(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  fullWidth
                  autoFocus
                  // placeholder={t('glossary.newFolder.placeholder') || '新建文件夹名称'}
                />
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => { e.stopPropagation(); onConfirmAddFolder && onConfirmAddFolder(); }}
                  aria-label="confirm-add-folder"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CheckOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => { e.stopPropagation(); onCancelAddFolder && onCancelAddFolder(); }}
                  aria-label="cancel-add-folder"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          }
          const isFolder = typeof item.type === 'string' && item.type === 'folder';
          if (isFolder && editingFolderId === item.uuid) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <FolderIcon fontSize="small" />
                <TextField
                  size="small"
                  value={editingFolderName}
                  onChange={(e) => setEditingFolderName(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  fullWidth
                  autoFocus
                />
                <IconButton
                  size="small"
                  color="success"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const name = (editingFolderName || '').trim();
                    if (!name || !editingFolderId) { setEditingFolderId(null); setEditingFolderName(''); return; }
                    try {
                      console.log('Glossary: confirm rename', { id: editingFolderId, name });
                      await makeRequest({ method: 'PUT', url: `/api/v1/glossary/folders/${editingFolderId}/rename`, data: { name } });
                      console.log('Glossary: rename success, trigger onFolderRenamed');
                      onReloadExplorer && onReloadExplorer();
                      setEditingFolderId(null);
                      setEditingFolderName('');
                    } catch (err: any) {
                      const msg = err?.response?.data?.message ?? err?.data?.message ?? err?.message ?? String(err);
                      dispatch(app.actions.setGlobalMessage({ message: msg, status: 'error' }));
                    }
                  }}
                  aria-label="confirm-rename-folder"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CheckOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => { e.stopPropagation(); setEditingFolderId(null); setEditingFolderName(''); }}
                  aria-label="cancel-rename-folder"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          }
          const icon = isFolder ? <FolderIcon fontSize="small" /> : <DescriptionOutlinedIcon fontSize="small" />;
          const clickable = (isFolder && !!onEnterFolder) || (!isFolder && !!onNavigate);
          const showId = !isFolder && item.uuid !== '__draft__';
          return (
            <Box>
              <EllipsisCell
                maxLines={3}
                content={
                  <Link
                    component="span"
                    underline="hover"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!clickable) return;
                      if (isFolder) {
                        onEnterFolder && onEnterFolder({ uuid: item.uuid, name: item.name, parentId: item.parentId });
                      } else {
                        onNavigate && onNavigate(item as GlossaryCollectionDto);
                      }
                    }}
                    style={{ cursor: clickable ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {icon}
                    {item.name}
                  </Link>
                }
                tooltip={item.name}
              />
              {showId && <CopyableId id={item.uuid} />}
            </Box>
          );
        },
      },
      {
        id: 'description',
        label: t('table.description'),
        width: 200,
        render: (item: any) => (
          <EllipsisCell content={item.uuid === '__draft__' ? '-' : item.desc || '-'} maxLines={3} />
        ),
        hidden: isSmallScreen,
      },
      {
        id: 'terms',
        label: t('table.terms'),
        width: 100,
        render: (item: any) => {
          const isFolder = typeof item.type === 'string' && item.type === 'folder';
          if (item.uuid === '__draft__' || isFolder) return '-';
          return item.status === GlossaryCollectionDtoStatusEnum.Ready
            ? formatter.format(item.itemCount)
            : t('common.na');
        },
      },
      // {
      //   id: 'storage',
      //   label: t('table.storage'),
      //   render: (glossary: GlossaryCollectionDto) =>
      //     glossary.uuid === '__draft__'
      //       ? '-'
      //       : glossary.type === GlossaryCollectionDtoTypeEnum.Internal
      //       ? t('glossary.storage.inMemory')
      //       : t('glossary.storage.openSearch'),
      //   hidden: isMediumScreen,
      // },
      {
        id: 'updatedAt',
        label: t('table.updatedAt'),
        render: (item: any) => {
          const isFolder = typeof item.type === 'string' && item.type === 'folder';
          return item.uuid === '__draft__' || isFolder ? '-' : formatDate(item.updatedAt);
        },
        width: 180,
        hidden: isMediumScreen,
      },
      {
        id: 'status',
        label: t('table.status'),
        width: 100,
        render: (item: any) => {
          if (item.uuid === '__draft__') {
            return <Typography variant="body2">-</Typography>;
          }
          const isFolder = typeof item.type === 'string' && item.type === 'folder';
          if (isFolder) return <Typography variant="body2">-</Typography>;
          const content = (
            <Typography
              variant="body2"
              className={statusClassMapping[item.status]}
            >
              {statusIconMapping[item.status]}
              <span className="align-middle ml-1">
                {t(`glossary.status.${String(item.status).toLowerCase()}`)}
              </span>
            </Typography>
          );

          // 显示错误提示：只要存在 errorMessage 即展示，避免不同枚举值/大小写导致判断失败
          if (item.errorMessage) {
            return (
              <Tooltip title={item.errorMessage} placement="top" arrow>
                {content}
              </Tooltip>
            );
          }

          return content;
        },
        hidden: isSmallScreen,
      },
      {
        id: 'action',
        label: t('table.actions'),
        width: 200,
        render: (item: any) => {
          if (item.uuid === '__draft__') return '-';
          const isFolder = typeof item.type === 'string' && item.type === 'folder';
          // Read-only rows (e.g. "default" collections/folders for non-super-admins)
          // keep only non-mutating actions.
          const mutable = canMutateRow ? canMutateRow(item) : true;
          if (isFolder) {
            if (!mutable) return '-';
            return (
              <>
                <Tooltip title={t('table.tooltip.rename', { defaultValue: '重命名' })} placement="top">
                  <IconButton
                    color="primary"
                    aria-label="rename-folder"
                    onClick={(e) => { e.stopPropagation(); setEditingFolderId(item.uuid); setEditingFolderName(item.name || ''); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    disabled={rowActionLoading}
                  >
                    <EditOutlinedIcon/>
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('table.tooltip.delete')} placement="top">
                  <IconButton
                    color="primary"
                    aria-label="delete-folder"
                    onClick={(e) => { e.stopPropagation(); onDeleteFolderClick && onDeleteFolderClick(item.uuid); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    disabled={rowActionLoading}
                  >
                    <DeleteOutlineOutlined/>
                  </IconButton>
                </Tooltip>
              </>
            );
          }
          const glossary = item as GlossaryCollectionDto;
          return (
            <>
              <Tooltip title={t('table.tooltip.download')} placement="top">
                <IconButton
                  color="primary"
                  aria-label="download-glossary"
                  onClick={(e) => { e.stopPropagation(); onDownloadClick && onDownloadClick(glossary); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={rowActionLoading || glossary.status === GlossaryCollectionDtoStatusEnum.Importing}
                >
                  <FileDownloadOutlined/>
                </IconButton>
              </Tooltip>
              {mutable && (
              <Tooltip title={t('table.tooltip.move')} placement="top">
                <IconButton
                  color="primary"
                  onClick={(e) => { e.stopPropagation(); onMoveClick && onMoveClick(glossary); }}
                  aria-label="move-glossary"
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={rowActionLoading}
                >
                  <DriveFileMoveOutlined/>
                </IconButton>
              </Tooltip>
              )}
              {mutable && (
              <Tooltip title={t('table.tooltip.update')} placement="top">
                <IconButton
                  color="primary"
                  aria-label="update-glossary"
                  onClick={(e) => { e.stopPropagation(); onUpdateClick && onUpdateClick(glossary); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={rowActionLoading || glossary.status === GlossaryCollectionDtoStatusEnum.Importing}
                >
                  <SyncOutlined/>
                </IconButton>
              </Tooltip>
              )}
              {mutable && (
              <Tooltip title={t('table.tooltip.delete')} placement="top">
                <IconButton
                  color="primary"
                  aria-label="delete-glossary"
                  onClick={(e) => { e.stopPropagation(); onDeleteClick && onDeleteClick(glossary); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  disabled={rowActionLoading}
                >
                  <DeleteOutlineOutlined/>
                </IconButton>
              </Tooltip>
              )}
            </>
          );
        },
        hidden: !showActions,
      }
    ],
    [
      isSmallScreen,
      isMediumScreen,
      onNavigate,
      onMoveClick,
      onDownloadClick,
      onDeleteClick,
      onUpdateClick,
      showActions,
      canMutateRow,
      rowActionLoading,
      editingFolderId,
      editingFolderName,
    ],
  );

  const visibleColumns = columns.filter((column) => !column.hidden);

  return (
    <PaginatedTable
      // mixedItems 表示使用服务端分页，此时不进行前端切片
      dummyPagination={!mixedItems}
      totalCount={mixedItems ? totalCount : undefined}
      columns={visibleColumns}
      data={displayItems}
      selectedItems={enableSelection ? [...selectedGlossaries, ...selectedFolders] : undefined}
      page={page}
      rowsPerPage={rowsPerPage}
      loading={glossary.loading}
      getItemId={enableSelection ? (item: any) => item.uuid : undefined}
      onSelectAllClick={enableSelection ? handleSelectAllClick : undefined}
      onItemClick={enableSelection ? handleClick : undefined}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};
